import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import { formatAge } from '@/utils/ageHelper';
import dayjs from 'dayjs';
import {
	useDeleteMemberApplicationMutation,
	useGetMemberApplicationDetailQuery,
} from '@/api/forms-management/memberApplication';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
	ACCEPT,
	EMERGENCY_CONTACT,
	LANGUAGE_LIST,
	LANGUAGE_ZH_LIST,
} from '@/common/constants/common';
import { showLabel } from '@/utils/helper';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
	useGetExportCustomerPDFMutation,
	useGetMemberApplicationGenerateReportMutation,
} from '@/api/common';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function MemberApplicationDetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, memberApplicationId, admissionRequestId } =
		useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteMemberApplication] = useDeleteMemberApplicationMutation();
	const showModal = useModal();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getMemberApplicationGenerateReport] =
		useGetMemberApplicationGenerateReportMutation();

	const [informationExpanded, setInformationExpanded] = useState(true);
	const [contactExpanded, setContactExpanded] = useState(true);
	const [urgencyExpanded, setUrgencyExpanded] = useState(true);
	const [workExpanded, setWorkExpanded] = useState(true);
	const [healthExpanded, setHealthExpanded] = useState(true);
	const [applyExpanded, setApplyExpanded] = useState(true);
	const [centerExpanded, setCenterExpanded] = useState(true);

	const { data: details }: any = useGetMemberApplicationDetailQuery(
		memberApplicationId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.memberApplicationForm'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteMemberApplication(
						details?.data.id || ''
					);
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.memberApplicationDetail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getMemberApplicationGenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.zh_serviceDetails')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.memberNumber')}
								value={details?.data.fileNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.unit')}
								value={details?.data.branchName}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setInformationExpanded(!informationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.memberInfo')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: informationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={informationExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.en_name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_name_en')}
									value={details?.data.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.birthday')}
									value={
										details?.data.dateOfBirth
											? dayjs(details?.data.dateOfBirth)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_age')}
									value={formatAge(
										details?.data.currentAge,
										details?.data.currentAgeEstimated,
										'zh'
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_sex')}
									value={details?.data.sexDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.idNumber')}
									value={details?.data.hkid}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_maritalStatus')}
									value={details?.data.maritalStatusDesc}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setContactExpanded(!contactExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.contactInfo')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: contactExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={contactExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.phone')}
									value={details?.data.phone}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.homePhone')}
									value={details?.data.homeTel}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.zh_email')}
									value={details?.data.email}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.zh_addressLanguage')}
									value={showLabel(
										details?.data.addressLanguage,
										LANGUAGE_ZH_LIST
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.territory')}
									value={details?.data.regionDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.area')}
									value={details?.data.districtDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.village')}
									value={details?.data.subdistrictDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.estate')}
									value={details?.data.estateCourtVillageDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.street')}
									value={details?.data.roadStreet}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.streetNumber')}
									value={details?.data.roadStreetNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.block')}
									value={details?.data.buildingBlockDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.blockDetail')}
									value={details?.data.buildingBlockSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.storey')}
									value={details?.data.floor}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.chamber')}
									value={details?.data.flat}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_address')}
									value={details?.data.fullAddress}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setUrgencyExpanded(!urgencyExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.emergencyContactMan')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: urgencyExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={urgencyExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.isEmergencyContact')}
									value={showLabel(
										details?.data.hasEmergencyContact,
										EMERGENCY_CONTACT
									)}
								/>
							</Grid>
							{details?.data.hasEmergencyContact && (
								<>
									<Grid size={6}>
										<AppFieldView
											value={details?.data.emergencyContactName}
											label={t('preAdmission.emergencyContactName')}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											value={details?.data.emergencyContactAge}
											label={t('preAdmission.emergencyContactAge')}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											value={details?.data.emergencyContactRelationshipDesc}
											label={t('preAdmission.emergencyContactRelationship')}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											value={details?.data.emergencyContactAddress}
											label={t('preAdmission.emergencyContactAddress')}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											value={details?.data.emergencyContactTelNo}
											label={t('preAdmission.emergencyContactTelNo')}
										/>
									</Grid>
								</>
							)}
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setWorkExpanded(!workExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.educationalWorkLive')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: workExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={workExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_education')}
									value={details?.data.educationDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									value={details?.data.workStatusDesc}
									label={t('preAdmission.occupation')}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									value={details?.data.livingStatusDesc}
									label={t('preAdmission.liveStatus')}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									value={details?.data.financialConditionDesc}
									label={t('preAdmission.sourceFinance')}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setHealthExpanded(!healthExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.zh_mentalHealthInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: healthExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={healthExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.psychiatricLocation')}
									value={details?.data.followUpClinicPsychiatryDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									value={details?.data.psychiatricDisordersTypeDesc}
									label={t('preAdmission.mentalIllnessCategory')}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									value={details?.data.psychiatricDisordersTypeSpecify}
									label={t('preAdmission.mentalIllnessCategoryDetail')}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									value={details?.data.rehabilitationServicesWaitingDesc}
									label={t('preAdmission.rehabilitationServices')}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									value={details?.data.rehabilitationServicesWaitingSpecify}
									label={t('preAdmission.rehabilitationServicesDetail')}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setApplyExpanded(!applyExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.consentForm')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: applyExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={applyExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.postalPreference')}
									value={
										details?.data.mailRequest
											? t('preAdmission.isConsentFormTitle')
											: t('preAdmission.noConsentFormTitle')
									}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.applicantSignatureDate')}
									value={
										details?.data.applicantSignatureDate
											? dayjs(details?.data.applicantSignatureDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.parentGuardianName')}
									value={details?.data.guardianName || ''}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.parentGuardianSignatureDate')}
									value={
										details?.data.guardianSignatureDate
											? dayjs(details?.data.guardianSignatureDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setCenterExpanded(!centerExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.acceptApplicationForm')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: centerExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={centerExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.acceptApplication')}
									value={showLabel(details?.data.acceptApplication, ACCEPT)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_approachSource')}
									value={details?.data.approachSourceDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referredOrganisation')}
									value={details?.data.referredOrganisation}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mentalHealthConditionList')}
									value={details?.data.mentalHealthConditionDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.serviceRecommendedList')}
									value={details?.data.serviceRecommendedDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.serviceRecommendedListDetail')}
									value={details?.data.serviceRecommendedSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.membershipApplicationRemarks')}
									value={details?.data.membershipApplicationRemarks}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.interventionWorker')}
									value={details?.data.interventionWorkerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.meetingDate')}
									value={
										details?.data.meetingDate
											? dayjs(details?.data.meetingDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.onDutyStaffSignatureDate')}
									value={
										details?.data.onDutyStaffSignatureDate
											? dayjs(details?.data.onDutyStaffSignatureDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
